'use client';

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  Rocket, TrendingUp, Eye, Search, Shield,
  BarChart3, Sparkles, Target, Zap, Users,
  CheckCircle2, ArrowRight, ArrowLeft, ArrowDown,
  Lock, Bell, Star,
} from 'lucide-react';

const STARTUP_STEPS = [
  {
    step: '01', icon: Rocket, title: 'Build Profile',
    desc: 'Create a structured fundability profile with stage, traction & milestones.',
    color: 'from-purple-500 to-violet-600',
    light: 'bg-purple-50 text-purple-700 border-purple-200',
    tags: ['Traction', 'Milestones', 'Stage'],
  },
  {
    step: '02', icon: Eye, title: 'Gain Visibility',
    desc: 'Surface automatically to investors aligned with your sector & readiness score.',
    color: 'from-violet-500 to-fuchsia-600',
    light: 'bg-violet-50 text-violet-700 border-violet-200',
    tags: ['Deal Flow', 'Signals', 'Scoring'],
  },
  {
    step: '03', icon: Bell, title: 'Express Interest',
    desc: 'Send structured interest signals to investors. Await curated introductions.',
    color: 'from-fuchsia-500 to-pink-600',
    light: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
    tags: ['Signal', 'Interest', 'Invite'],
  },
  {
    step: '04', icon: Target, title: 'Close the Loop',
    desc: 'Engage through structured conversations, share data securely & track outcomes.',
    color: 'from-pink-500 to-rose-600',
    light: 'bg-pink-50 text-pink-700 border-pink-200',
    tags: ['Secure', 'Track', 'Outcome'],
  },
];

const INVESTOR_STEPS = [
  {
    step: '01', icon: TrendingUp, title: 'Define Thesis',
    desc: 'Set your investment thesis, sector preferences, cheque size & stage alignment.',
    color: 'from-amber-500 to-orange-600',
    light: 'bg-amber-50 text-amber-700 border-amber-200',
    tags: ['Thesis', 'Sector', 'Stage'],
  },
  {
    step: '02', icon: Search, title: 'Discover Deals',
    desc: 'Browse curated startups filtered by readiness signals, sector & fundability scores.',
    color: 'from-orange-500 to-red-500',
    light: 'bg-orange-50 text-orange-700 border-orange-200',
    tags: ['Filter', 'Discover', 'Curated'],
  },
  {
    step: '03', icon: BarChart3, title: 'Analyse & Rate',
    desc: 'Review profiles, rate startups, request additional data through secure channels.',
    color: 'from-red-500 to-rose-600',
    light: 'bg-red-50 text-red-700 border-red-200',
    tags: ['Review', 'Rate', 'Due Dil.'],
  },
  {
    step: '04', icon: Star, title: 'Shortlist & Fund',
    desc: 'Shortlist top opportunities, initiate conversations & manage your deal pipeline.',
    color: 'from-rose-500 to-pink-600',
    light: 'bg-rose-50 text-rose-700 border-rose-200',
    tags: ['Shortlist', 'Converse', 'Fund'],
  },
];

const CENTER_NODES = [
  { icon: Zap,    label: 'Compatibility Mapping', sub: 'AI-driven alignment' },
  { icon: Shield, label: 'Signal Discovery',       sub: 'Readiness-based matching' },
  { icon: Users,  label: 'Guided Interaction',     sub: 'Structured engagement' },
  { icon: Lock,   label: 'Controlled Sharing',     sub: 'Permission-gated data' },
];

const STATS = [
  { value: '3×',   label: 'Faster discovery' },
  { value: '85%',  label: 'Match accuracy' },
  { value: '100%', label: 'Invite-only quality' },
  { value: '48h',  label: 'Avg. first response' },
];

/* ─── Step Card ──────────────────────────────────────────── */
function StepCard({
  step, index, isVisible, side,
}: {
  step: typeof STARTUP_STEPS[0];
  index: number;
  isVisible: boolean;
  side: 'startup' | 'investor';
}) {
  const [hovered, setHovered] = useState(false);
  const Icon = step.icon;

  return (
    <div
      className={cn(
        'relative transition-all duration-700',
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6',
      )}
      style={{ transitionDelay: `${index * 120}ms` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={cn(
        'relative rounded-2xl border bg-white overflow-hidden transition-all duration-300 h-full',
        hovered ? 'shadow-xl -translate-y-1 border-transparent' : 'shadow-sm border-gray-100',
      )}>
        {/* Gradient top stripe */}
        <div className={cn('h-1 w-full bg-gradient-to-r', step.color)} />

        <div className="p-4 sm:p-5">
          {/* Step number + icon row */}
          <div className="flex items-start justify-between mb-3">
            <div className={cn(
              'w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br shadow-md transition-transform duration-300',
              step.color,
              hovered ? 'scale-110' : 'scale-100',
            )}>
              <Icon className="w-4 h-4 text-white" />
            </div>
            <span className={cn(
              'text-2xl font-black leading-none transition-colors duration-300',
              hovered ? 'text-gray-200' : 'text-gray-100',
            )}>
              {step.step}
            </span>
          </div>

          <h4 className="font-bold text-gray-900 text-sm mb-1 leading-snug">{step.title}</h4>
          <p className="text-xs text-gray-500 leading-relaxed mb-3">{step.desc}</p>

          <div className="flex flex-wrap gap-1">
            {step.tags.map((tag) => (
              <span key={tag} className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full border', step.light)}>
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Desktop: between-card arrows — hidden on mobile */}
      {index < 2 && side === 'startup' && (
        <div className="hidden xl:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-7 h-7 rounded-full bg-white shadow-md border border-purple-100">
          <ArrowRight className="w-3 h-3 text-purple-400" />
        </div>
      )}
      {index < 2 && side === 'investor' && (
        <div className="hidden xl:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-7 h-7 rounded-full bg-white shadow-md border border-amber-100">
          <ArrowLeft className="w-3 h-3 text-amber-400" />
        </div>
      )}
      {/* Desktop: final arrow pointing to center */}
      {index === 3 && side === 'startup' && (
        <div className="hidden lg:flex absolute -right-5 top-1/2 -translate-y-1/2 z-10 items-center gap-1">
          <div className="h-px w-5 bg-gradient-to-r from-purple-300 to-purple-500" />
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-lg ring-2 ring-white">
            <ArrowRight className="w-3.5 h-3.5 text-white" />
          </div>
        </div>
      )}
      {index === 3 && side === 'investor' && (
        <div className="hidden lg:flex absolute -left-5 top-1/2 -translate-y-1/2 z-10 items-center gap-1">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg ring-2 ring-white">
            <ArrowLeft className="w-3.5 h-3.5 text-white" />
          </div>
          <div className="h-px w-5 bg-gradient-to-l from-amber-300 to-amber-500" />
        </div>
      )}
    </div>
  );
}

/* ─── Mobile timeline row ────────────────────────────────── */
function MobileStepRow({
  step, index, isVisible, side,
}: {
  step: typeof STARTUP_STEPS[0];
  index: number;
  isVisible: boolean;
  side: 'startup' | 'investor';
}) {
  const Icon = step.icon;
  const isLast = index === 3;
  const isStartup = side === 'startup';

  return (
    <div
      className={cn(
        'flex gap-3 transition-all duration-700',
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
      )}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Timeline spine */}
      <div className="flex flex-col items-center shrink-0">
        <div className={cn(
          'w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br shadow-md shrink-0',
          step.color,
        )}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        {!isLast && (
          <div className={cn(
            'w-px flex-1 mt-1 min-h-[24px]',
            isStartup ? 'bg-purple-200' : 'bg-amber-200',
          )} />
        )}
      </div>

      {/* Content */}
      <div className={cn('pb-4 flex-1', isLast ? 'pb-0' : '')}>
        <div className="flex items-center gap-2 mb-0.5">
          <span className={cn(
            'text-[10px] font-bold px-1.5 py-0.5 rounded-full',
            isStartup ? 'bg-purple-100 text-purple-600' : 'bg-amber-100 text-amber-600',
          )}>
            {step.step}
          </span>
          <h4 className="font-bold text-gray-900 text-sm">{step.title}</h4>
        </div>
        <p className="text-xs text-gray-500 leading-relaxed mb-2">{step.desc}</p>
        <div className="flex flex-wrap gap-1">
          {step.tags.map((tag) => (
            <span key={tag} className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full border', step.light)}>
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Center Node ────────────────────────────────────────── */
function CenterNode({ node, index, isVisible }: {
  node: typeof CENTER_NODES[0];
  index: number;
  isVisible: boolean;
}) {
  const Icon = node.icon;
  return (
    <div
      className={cn('flex flex-col items-center gap-1.5 transition-all duration-700', isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90')}
      style={{ transitionDelay: `${index * 120 + 200}ms` }}
    >
      <div className="w-11 h-11 rounded-xl bg-white/20 border border-white/30 flex items-center justify-center backdrop-blur-sm">
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="text-center px-1">
        <p className="text-[11px] font-bold text-white leading-tight">{node.label}</p>
        <p className="text-[10px] text-purple-200 leading-snug">{node.sub}</p>
      </div>
    </div>
  );
}

/* ─── Main Section ───────────────────────────────────────── */
export default function HowItWorksFlowSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="how-it-works-flow"
      ref={sectionRef}
      className="relative py-14 sm:py-20 lg:py-28 overflow-hidden"
      style={{ background: 'linear-gradient(180deg,#fafafa 0%,#ffffff 40%,#f5f3ff 100%)' }}
    >
      {/* Bg blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 -left-20 w-72 sm:w-96 h-72 sm:h-96 bg-purple-100/40 rounded-full blur-3xl" />
        <div className="absolute top-1/3 right-0 w-60 sm:w-80 h-60 sm:h-80 bg-violet-100/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/3 w-56 sm:w-72 h-56 sm:h-72 bg-fuchsia-100/30 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Header ── */}
        <div className={cn(
          'text-center mb-10 sm:mb-14 transition-all duration-700',
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5',
        )}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold mb-4 border border-purple-200">
            <Sparkles className="w-3.5 h-3.5" />
            Structured Matchmaking
          </div>
          <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-gray-900 mb-3 leading-tight">
            Two journeys.{' '}
            <span className="bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">
              One aligned process.
            </span>
          </h2>
          <p className="text-sm sm:text-base text-gray-500 max-w-xl mx-auto px-2">
            Startups build readiness. Investors discover quality deals. Purple structures the path between them.
          </p>
        </div>

        {/* ── Stats bar ── */}
        <div className={cn(
          'grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10 sm:mb-14 transition-all duration-700 delay-100',
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5',
        )}>
          {STATS.map((s) => (
            <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3 text-center">
              <div className="text-xl sm:text-2xl font-black bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent mb-0.5">
                {s.value}
              </div>
              <div className="text-[11px] sm:text-xs text-gray-500 font-medium">{s.label}</div>
            </div>
          ))}
        </div>

        {/* ══════════════════════════════════════════════
            MOBILE LAYOUT  (<lg)
            Vertical stacked: Startup → Platform → Investor
        ══════════════════════════════════════════════ */}
        <div className="lg:hidden space-y-6">

          {/* Startup Journey */}
          <div className={cn(
            'rounded-2xl border border-purple-100 bg-white shadow-sm overflow-hidden transition-all duration-700 delay-150',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
          )}>
            {/* Header strip */}
            <div className="bg-gradient-to-r from-purple-500 to-violet-600 px-4 py-3 flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
                <Rocket className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white font-bold text-sm leading-none">Startup Journey</p>
                <p className="text-purple-200 text-[11px]">Build · Signal · Engage</p>
              </div>
            </div>
            <div className="p-4 space-y-0">
              {STARTUP_STEPS.map((step, i) => (
                <MobileStepRow key={step.step} step={step} index={i} isVisible={isVisible} side="startup" />
              ))}
            </div>
          </div>

          {/* Arrow down to platform */}
          <div className={cn(
            'flex items-center justify-center gap-2 transition-all duration-700 delay-300',
            isVisible ? 'opacity-100' : 'opacity-0',
          )}>
            <ArrowDown className="w-5 h-5 text-purple-400 animate-bounce" />
            <span className="text-xs font-semibold text-purple-600 bg-purple-50 border border-purple-200 px-3 py-1 rounded-full">
              Meets Purple Platform
            </span>
            <ArrowDown className="w-5 h-5 text-purple-400 animate-bounce" />
          </div>

          {/* Platform Spine — horizontal scroll on mobile */}
          <div className={cn(
            'rounded-2xl bg-gradient-to-br from-purple-600 to-violet-700 shadow-xl shadow-purple-500/25 p-5 transition-all duration-700 delay-350',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
          )}>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-white" />
              <p className="text-white font-bold text-sm">Purple Platform</p>
              <div className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/15 border border-white/20">
                <CheckCircle2 className="w-3 h-3 text-white" />
                <span className="text-[10px] text-white font-semibold">Invite Only</span>
              </div>
            </div>
            {/* 2x2 grid on mobile */}
            <div className="grid grid-cols-2 gap-3">
              {CENTER_NODES.map((node, i) => {
                const Icon = node.icon;
                return (
                  <div key={node.label} className="flex items-start gap-2.5 bg-white/10 rounded-xl p-3 border border-white/15">
                    <div className="w-8 h-8 rounded-lg bg-white/20 border border-white/30 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-[11px] font-bold text-white leading-tight">{node.label}</p>
                      <p className="text-[10px] text-purple-200 leading-snug mt-0.5">{node.sub}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Arrow down to investor */}
          <div className={cn(
            'flex items-center justify-center gap-2 transition-all duration-700 delay-400',
            isVisible ? 'opacity-100' : 'opacity-0',
          )}>
            <ArrowDown className="w-5 h-5 text-amber-400 animate-bounce" />
            <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full">
              Connects Investors
            </span>
            <ArrowDown className="w-5 h-5 text-amber-400 animate-bounce" />
          </div>

          {/* Investor Journey */}
          <div className={cn(
            'rounded-2xl border border-amber-100 bg-white shadow-sm overflow-hidden transition-all duration-700 delay-450',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
          )}>
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-4 py-3 flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white font-bold text-sm leading-none">Investor Journey</p>
                <p className="text-amber-100 text-[11px]">Discover · Analyse · Fund</p>
              </div>
            </div>
            <div className="p-4 space-y-0">
              {INVESTOR_STEPS.map((step, i) => (
                <MobileStepRow key={step.step} step={step} index={i} isVisible={isVisible} side="investor" />
              ))}
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════════
            DESKTOP LAYOUT  (lg+)
            3-column: Startup | Spine | Investor
        ══════════════════════════════════════════════ */}
        <div className="hidden lg:block">

          {/* Journey labels row */}
          <div className="grid lg:grid-cols-[1fr_180px_1fr] gap-6 lg:gap-8 mb-5">
            <div className={cn(
              'flex items-center gap-3 transition-all duration-700 delay-150',
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4',
            )}>
              <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-md shrink-0">
                <Rocket className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="font-bold text-sm text-gray-900">Startup Journey</p>
                <p className="text-xs text-gray-400">Build · Signal · Engage</p>
              </div>
              <div className="flex-1 h-px bg-gradient-to-r from-purple-200 to-transparent ml-2" />
            </div>

            <div className="flex items-center justify-center">
              <div className="px-3 py-1.5 rounded-full bg-gradient-to-r from-purple-600 to-violet-600 text-white text-[11px] font-bold shadow-lg shadow-purple-500/20">
                Purple Platform
              </div>
            </div>

            <div className={cn(
              'flex items-center gap-3 flex-row-reverse transition-all duration-700 delay-150',
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4',
            )}>
              <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-md shrink-0">
                <TrendingUp className="w-4 h-4 text-white" />
              </div>
              <div className="text-right">
                <p className="font-bold text-sm text-gray-900">Investor Journey</p>
                <p className="text-xs text-gray-400">Discover · Analyse · Fund</p>
              </div>
              <div className="flex-1 h-px bg-gradient-to-l from-amber-200 to-transparent mr-2" />
            </div>
          </div>

          {/* 3-col grid */}
          <div className="grid lg:grid-cols-[1fr_180px_1fr] gap-6 lg:gap-8">

            {/* Left: Startup 2×2 */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {STARTUP_STEPS.map((step, i) => (
                <StepCard key={step.step} step={step} index={i} isVisible={isVisible} side="startup" />
              ))}
            </div>

            {/* Center spine */}
            <div className={cn(
              'flex flex-col items-center gap-2 transition-all duration-700 delay-300',
              isVisible ? 'opacity-100' : 'opacity-0',
            )}>
              <div className="w-px flex-1 bg-gradient-to-b from-transparent via-purple-300 to-purple-400 max-h-8" />
              <div className="w-full rounded-3xl bg-gradient-to-b from-purple-600 to-violet-700 shadow-xl shadow-purple-500/25 p-5 flex flex-col gap-0">
                {CENTER_NODES.map((node, i) => (
                  <div key={node.label}>
                    <CenterNode node={node} index={i} isVisible={isVisible} />
                    {i < CENTER_NODES.length - 1 && (
                      <div className="w-px h-4 bg-white/20 mx-auto my-1" />
                    )}
                  </div>
                ))}
              </div>
              <div className="w-px flex-1 bg-gradient-to-b from-violet-400 via-purple-300 to-transparent max-h-8" />
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-purple-200 shadow-sm">
                <CheckCircle2 className="w-3.5 h-3.5 text-purple-600" />
                <span className="text-[11px] font-bold text-purple-700">Invite Only</span>
              </div>
            </div>

            {/* Right: Investor 2×2 */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {INVESTOR_STEPS.map((step, i) => (
                <StepCard key={step.step} step={step} index={i} isVisible={isVisible} side="investor" />
              ))}
            </div>
          </div>
        </div>

        {/* ── Bottom CTA banner ── */}
        <div className={cn(
          'mt-10 sm:mt-16 rounded-2xl sm:rounded-3xl overflow-hidden transition-all duration-700 delay-500',
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6',
        )}>
          <div className="relative bg-gradient-to-r from-purple-600 via-violet-600 to-fuchsia-600 p-6 sm:p-8 lg:p-10">
            <div className="absolute inset-0 opacity-10"
              style={{ backgroundImage: 'radial-gradient(circle,white 1px,transparent 1px)', backgroundSize: '24px 24px' }}
            />
            <div className="relative flex flex-col sm:flex-row items-center justify-between gap-5">
              <div className="text-center sm:text-left">
                <p className="text-white/70 text-xs sm:text-sm font-medium mb-1">Ready to get started?</p>
                <h3 className="text-white font-display font-bold text-xl sm:text-2xl lg:text-3xl leading-tight">
                  Two entry points.{' '}
                  <span className="text-purple-200">One structured process.</span>
                </h3>
                <p className="text-white/60 text-xs sm:text-sm mt-1.5">
                  Join as a Startup or Investor — invite code required.
                </p>
              </div>
              <div className="flex flex-col xs:flex-row sm:flex-col lg:flex-row gap-2.5 shrink-0 w-full sm:w-auto">
                <a href="/apply/founder"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white text-purple-700 font-bold text-sm rounded-xl hover:bg-purple-50 transition-colors shadow-lg whitespace-nowrap">
                  <Rocket className="w-4 h-4" /> Apply as Startup
                </a>
                <a href="/apply/investor"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white/10 text-white font-bold text-sm rounded-xl border border-white/20 hover:bg-white/20 transition-colors whitespace-nowrap">
                  <TrendingUp className="w-4 h-4" /> Apply as Investor
                </a>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
